import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Wallet, Building2, TrendingUp, TrendingDown, Banknote, Landmark,
  ArrowRight, Calculator, FileText, Loader2, Receipt, Smartphone,
  Settings, PiggyBank, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime as fmtDateTimeTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] ?? false;
};

function Treasury() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cashBanks, setCashBanks] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Movements dialog
  const [movementsDialogOpen, setMovementsDialogOpen] = useState(false);
  const [selectedCashBank, setSelectedCashBank] = useState(null);
  const [cashBankMovements, setCashBankMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin' || userCan(user, 'tesoreria_panel', 'ver')) fetchData();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const [cbRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treasury/cash-banks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/stats`, { withCredentials: true })
      ]);
      setCashBanks(cbRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Error al cargar datos'); } finally { setLoading(false); }
  };

  const openCashBankMovements = async (cb) => {
    setSelectedCashBank(cb);
    setMovementsDialogOpen(true);
    setLoadingMovements(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/treasury/movements?cash_bank_id=${cb.cash_bank_id}&limit=50`, { withCredentials: true });
      setCashBankMovements(res.data);
    } catch { toast.error('Error al cargar movimientos'); setCashBankMovements([]); }
    finally { setLoadingMovements(false); }
  };

  const getEntityTypeIcon = (type) => {
    switch (type) { case 'cash': return <Banknote className="w-4 h-4" />; case 'bank': return <Landmark className="w-4 h-4" />; case 'wallet': return <Smartphone className="w-4 h-4" />; case 'credit_card': return <CreditCard className="w-4 h-4" />; default: return <Wallet className="w-4 h-4" />; }
  };
  const getEntityTypeName = (type) => {
    switch (type) { case 'cash': return 'Caja Fisica'; case 'bank': return 'Cuenta Bancaria'; case 'wallet': return 'Billetera Virtual'; case 'credit_card': return 'Tarjeta de Credito'; default: return type; }
  };
  const formatCurrency = (amount, currency = 'ARS') =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  const formatDateTime = (dateStr) => (dateStr ? fmtDateTimeTz(dateStr) : '-');

  const MOVEMENT_TYPE_LABELS = {
    collection: 'Cobranza', payment_order: 'Orden de Pago', adjustment: 'Ajuste',
    transfer_out: 'Transf. Salida', transfer_in: 'Transf. Entrada',
    check_deposit: 'Deposito Cheque', check_return: 'Cheque Rechazado',
    reconciliation_adjustment: 'Ajuste Conciliacion', cash_count_adjustment: 'Ajuste Arqueo'
  };

  // Filtrar cajas/bancos segun permisos del usuario
  const visibleCashBanks = cashBanks.filter(cb => {
    if (!cb.is_active) return false;
    if (user.role === 'admin') return true;
    if (!user.allowed_cash_banks || user.allowed_cash_banks.length === 0) return true;
    return user.allowed_cash_banks.includes(cb.cash_bank_id);
  });

  const canViewConfig = userCan(user, 'tesoreria_config', 'ver');
  const canViewReports = userCan(user, 'tesoreria_reportes', 'ver');
  const canViewCierres = userCan(user, 'tesoreria_arqueos', 'ver') || userCan(user, 'tesoreria_conciliaciones', 'ver');
  const canPanel = userCan(user, 'tesoreria_panel', 'ver');
  const showStats = user.role === 'admin' || user.role === 'supervisor';

  if (loading) return <div className="flex items-center justify-center h-64"><p>Cargando...</p></div>;

  return (
    <div className="space-y-6" data-testid="treasury-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Tesoreria</h1>
          <p className="text-muted-foreground">Gestion de cajas, bancos y operaciones</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canViewConfig && (
            <Button variant="outline" onClick={() => navigate('/treasury/config')} data-testid="treasury-config-btn">
              <Settings className="w-4 h-4 mr-2" />Configuracion
            </Button>
          )}
          {canViewCierres && (
            <Button variant="outline" onClick={() => navigate('/treasury/control')} data-testid="treasury-control-btn">
              <Calculator className="w-4 h-4 mr-2" />Cierres
            </Button>
          )}
          <Button onClick={() => navigate('/treasury/operations')} data-testid="treasury-operations-btn">
            <Receipt className="w-4 h-4 mr-2" />Operaciones<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Cajas y Bancos — requiere permiso Panel */}
      {!canPanel ? (
        <Card data-testid="panel-no-permission">
          <CardContent className="py-10 text-center text-muted-foreground">
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No tenés permiso para ver los saldos del Panel de Tesorería.</p>
            <p className="text-sm mt-1">Pedile a un administrador el permiso "Panel de Tesorería → Ver" si lo necesitás.</p>
          </CardContent>
        </Card>
      ) : (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5" /> Cajas y Bancos</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Moneda</TableHead>
                <TableHead className="text-right">Saldo Actual</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Movimientos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleCashBanks.map(cb => (
                <TableRow key={cb.cash_bank_id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">{getEntityTypeIcon(cb.entity_type)}{cb.name}</div>
                  </TableCell>
                  <TableCell>{getEntityTypeName(cb.entity_type)}</TableCell>
                  <TableCell><Badge variant="outline">{cb.currency}</Badge></TableCell>
                  <TableCell className={`text-right font-bold ${cb.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(cb.current_balance, cb.currency)}
                  </TableCell>
                  <TableCell className="text-center"><Badge className="bg-green-100 text-green-800">Activa</Badge></TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => openCashBankMovements(cb)} data-testid={`view-movements-${cb.cash_bank_id}`}>
                      Ver <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {visibleCashBanks.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay cajas/bancos activas</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Resumen rapido - solo para admin/supervisor */}
      {canPanel && showStats && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-green-600" />
              <p className="text-xs text-muted-foreground">Ingresos del mes</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(stats.monthly_totals?.total_debit || 0)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <TrendingDown className="w-5 h-5 mx-auto mb-1 text-red-600" />
              <p className="text-xs text-muted-foreground">Egresos del mes</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(stats.monthly_totals?.total_credit || 0)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <PiggyBank className="w-5 h-5 mx-auto mb-1 text-blue-600" />
              <p className="text-xs text-muted-foreground">Saldo Total</p>
              <p className="text-lg font-bold text-blue-600">{formatCurrency(Object.values(stats.totals_by_currency || {}).reduce((a, b) => a + b, 0))}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <Receipt className="w-5 h-5 mx-auto mb-1 text-purple-600" />
              <p className="text-xs text-muted-foreground">Operaciones del mes</p>
              <p className="text-lg font-bold">{stats.monthly_totals?.count || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog Movimientos */}
      <Dialog open={movementsDialogOpen} onOpenChange={setMovementsDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[1600px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCashBank && getEntityTypeIcon(selectedCashBank.entity_type)}
              Movimientos - {selectedCashBank?.name}
              {selectedCashBank && <Badge variant="outline" className="ml-2">{selectedCashBank.currency}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {selectedCashBank && (
            <div className="flex gap-4 mb-2">
              <div className="px-3 py-1.5 bg-muted rounded text-sm">
                <span className="text-muted-foreground">Saldo actual: </span>
                <span className={`font-bold ${selectedCashBank.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(selectedCashBank.current_balance, selectedCashBank.currency)}
                </span>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {loadingMovements ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : cashBankMovements.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No hay movimientos</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Comprobante</TableHead>
                  <TableHead>Concepto</TableHead><TableHead>Usuario</TableHead>
                  <TableHead className="text-right">Debe</TableHead><TableHead className="text-right">Haber</TableHead><TableHead className="text-right">Saldo</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {cashBankMovements.map((m, i) => (
                    <TableRow key={m.movement_id || i} className={m.deleted_at ? 'opacity-60' : ''} data-testid={`cb-movement-${m.movement_id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{formatDateTime(m.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}</Badge>
                        {m.deleted_at && (
                          <span className="ml-1 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`cb-movement-deleted-${m.movement_id}`}>ELIMINADO</span>
                        )}
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${m.deleted_at ? 'line-through' : ''}`}>{m.reference_number || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {m.concept_name || '-'}
                        {m.deleted_at && (
                          <span className="block text-xs text-muted-foreground">Eliminado por {m.deleted_by_name} · {m.deletion_reason}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{m.created_by_name || m.created_by || '-'}</TableCell>
                      <TableCell className={`text-right font-mono ${m.deleted_at ? 'text-muted-foreground line-through' : 'text-green-600'}`}>{m.debit > 0 ? formatCurrency(m.debit) : '-'}</TableCell>
                      <TableCell className={`text-right font-mono ${m.deleted_at ? 'text-muted-foreground line-through' : 'text-red-600'}`}>{m.credit > 0 ? formatCurrency(m.credit) : '-'}</TableCell>
                      <TableCell className={`text-right font-mono font-medium ${m.deleted_at ? 'text-muted-foreground' : (m.balance_after >= 0 ? 'text-green-600' : 'text-red-600')}`}>
                        {m.deleted_at || m.balance_after === null || m.balance_after === undefined ? '—' : formatCurrency(m.balance_after)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Treasury;
